import React, { useState } from "react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Shield, Calculator, Headphones, Building2, Map, Crown, LogIn,
  Users, Briefcase, Receipt, MessageSquare, Brain, FileText, ScrollText,
  LayoutDashboard, Zap, Upload, Download, AlertTriangle, Play, BookOpen, HelpCircle,
} from "lucide-react";

export default function Help() {
  const { user } = useAuth();
  const defaultTab = user?.role || "crm_manager";

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="help-page">
      <div>
        <div className="mptg-overline">User guide</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">How to use MPTG-CRM</h1>
        <p className="text-slate-500 mt-1 max-w-3xl">
          Quick, role-specific instructions for every person on the MAS Callnet team. Pick your role below — you can also read the
          sections for other roles to understand handoffs.
        </p>
      </div>

      {/* Getting started — common to all */}
      <section className="mptg-card">
        <div className="flex items-center gap-2 mb-3">
          <LogIn className="w-4 h-4 text-[#2E86C1]" />
          <h2 className="font-heading text-lg font-bold">Getting started (everyone)</h2>
        </div>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700">
          <li>Open the CRM URL and sign in with the email and password you were given.</li>
          <li>On desktop you'll see the <strong>navy sidebar</strong> on the left — that's the main menu. On mobile, tap the ☰ icon.</li>
          <li>Use the <strong>avatar (top-right)</strong> to <strong>Change password</strong> (self-serve) or <strong>Sign out</strong>.</li>
          <li>Green / Amber / Red badges on KPIs mean: within 5% of target / 5–15% off / more than 15% off target.</li>
          <li>Grade badges on clients: <span className="badge-green px-1.5 rounded text-[11px] font-semibold">A</span> best · <span className="badge-blue px-1.5 rounded text-[11px] font-semibold">B</span> good · <span className="badge-amber px-1.5 rounded text-[11px] font-semibold">C</span> watch · <span className="badge-red px-1.5 rounded text-[11px] font-semibold">D</span> at-risk.</li>
          <li>All changes are auto-logged in the <strong>Audit Log</strong> (admin only).</li>
        </ol>
      </section>

      {/* Password management */}
      <section className="mptg-card">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-[#2E86C1]" />
          <h2 className="font-heading text-lg font-bold">Password management</h2>
        </div>
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
          <li><strong>Change your own password</strong> any time: avatar (top-right) → <em>Change password</em>. Enter current + new + confirm. Takes effect immediately on next sign-in.</li>
          <li><strong>Reset someone else's password</strong> (CRM Manager only): <em>Sidebar → Admin</em>, find the user, click <em>Reset password</em>, set a temporary, and share via a secure channel. They can change it themselves after signing in.</li>
          <li><strong>Disable / re-enable a user</strong> (CRM Manager only): <em>Admin → Disable / Enable</em> on the user row. Disabled users cannot sign in but their historical data stays.</li>
          <li>Minimum password length is 6 characters. No self-serve "forgot password" yet — the CRM Manager resets it for you.</li>
        </ul>
      </section>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-white border border-slate-200 h-auto p-1 flex-wrap">
          <TabsTrigger value="crm_manager" data-testid="help-tab-crm"><Shield className="w-3.5 h-3.5 mr-1" /> CRM Manager</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="help-tab-accounts"><Calculator className="w-3.5 h-3.5 mr-1" /> Accounts</TabsTrigger>
          <TabsTrigger value="process_manager" data-testid="help-tab-pm"><Headphones className="w-3.5 h-3.5 mr-1" /> Process Manager</TabsTrigger>
          <TabsTrigger value="branch_manager" data-testid="help-tab-branch"><Building2 className="w-3.5 h-3.5 mr-1" /> Branch Manager</TabsTrigger>
          <TabsTrigger value="regional_manager" data-testid="help-tab-regional"><Map className="w-3.5 h-3.5 mr-1" /> Regional</TabsTrigger>
          <TabsTrigger value="coo_ceo" data-testid="help-tab-ceo"><Crown className="w-3.5 h-3.5 mr-1" /> COO / CEO</TabsTrigger>
        </TabsList>

        <TabsContent value="crm_manager" className="mt-4 space-y-3">
          <RoleBlock
            role="CRM Manager"
            why="You're the super-admin — you set up every client, user, contract and KPI. Everyone else only enters performance data into the structures you create."
            tasks={[
              {
                icon: Briefcase,
                title: "1. Create a client and its campaigns",
                steps: [
                  "Sidebar → Clients → New Client. Fill name, industry, HQ, primary contact, MBR / QBR email distribution lists.",
                  "Open the client → Campaigns tab → New campaign. Enter name, type (Inbound / Outbound / etc.), branch, region.",
                  "Assign a Process Manager from the dropdown (only users with the Process Manager role appear there).",
                  "Set the start and end dates and save. Campaign now shows up for the PM and Branch Manager.",
                ],
              },
              {
                icon: FileText,
                title: "2. Add the contract & SLAs",
                steps: [
                  "Client 360 → Contracts tab → New version. Set value, billing model, dates, renewal alert days.",
                  "The previous version is automatically archived (immutable history) — you can see both in the timeline.",
                ],
              },
              {
                icon: Play,
                title: "3. Define KPIs and update benchmarks",
                steps: [
                  "Open a campaign → Add KPI. Enter name (AHT, CSAT, FCR…), unit (%, sec, score), direction (higher/lower better), starting benchmark, effective date.",
                  "RAG is auto-calculated each month based on the actual entered by the Process Manager.",
                  "When a target changes (new SLA, new quarter), open the campaign → click 'Set benchmark' on the KPI row → enter the new value, effective date and reason.",
                  "The OLD benchmark stays in history — scorecards and trends stay accurate for the months that used the old target.",
                ],
              },
              {
                icon: Shield,
                title: "4. Manage users",
                steps: [
                  "Sidebar → Admin → New user. Pick role, branches, regions, password.",
                  "Process Managers see only their assigned campaigns; Branch Managers see their branch; Regional Managers see the branches in their region.",
                ],
              },
              {
                icon: Zap,
                title: "5. Run AI intelligence sweeps",
                steps: [
                  "Sidebar → Intelligence → Sweep all clients (or open a client and click 'Run intel sweep').",
                  "Adverse items automatically email the CRM Manager + COO/CEO (currently MOCKED in the email log).",
                ],
              },
              {
                icon: ScrollText,
                title: "6. Review the audit log",
                steps: [
                  "Sidebar → Audit Log shows every create / update / delete across the system with timestamp and user.",
                  "Use the search box to filter by table, user or action.",
                ],
              },
            ]}
            donts={[
              "Don't delete old KPI benchmarks — always add a new version so historical RAG stays accurate.",
              "Don't edit someone's billing directly — ask Accounts; they have the right workflow and bulk-upload tooling.",
            ]}
          />
        </TabsContent>

        <TabsContent value="accounts" className="mt-4 space-y-3">
          <RoleBlock
            role="Accounts Team"
            why="You own financial truth — monthly billing, collections, aging and profitability grades for every client."
            tasks={[
              {
                icon: Receipt,
                title: "Enter a single invoice",
                steps: [
                  "Sidebar → Billing → New invoice. Pick client, month, invoice number, amount, invoice date, amount received, payment date, profitability grade (A/B/C/D).",
                  "Aging bucket (current / 30 / 60 / 90+) is auto-calculated from the invoice date.",
                ],
              },
              {
                icon: Upload,
                title: "Bulk upload billing (month-end)",
                steps: [
                  "Billing → click Template — download the CSV. Fill one row per invoice.",
                  "Billing → Bulk upload → pick the file → Validate. Rows with errors are highlighted in red; fix and re-upload.",
                  "Click Commit N rows to import. A batch id is stored so you can trace which invoices came from which upload.",
                ],
              },
              {
                icon: AlertTriangle,
                title: "Track outstanding & set profitability",
                steps: [
                  "AR summary cards at the top show total invoiced / received / outstanding and the 30/60/90+ breakdown.",
                  "Filter by client + month to work one account at a time. Edit any record — changes are logged in the audit trail.",
                ],
              },
            ]}
            donts={[
              "Don't leave profitability grade blank — that hides the client from dashboards and risk reports.",
              "Don't reuse invoice numbers across months — the audit log keys on this.",
            ]}
          />
        </TabsContent>

        <TabsContent value="process_manager" className="mt-4 space-y-3">
          <RoleBlock
            role="Process Manager"
            why="You run the campaign floor. You enter monthly KPI actuals and record MBR minutes for your campaigns — the CRM Manager sees your RAG status immediately."
            tasks={[
              {
                icon: Briefcase,
                title: "Record monthly KPI actuals",
                steps: [
                  "Sidebar → Campaigns → open your campaign.",
                  "In the scorecard table, click Record (or Update) on each KPI. Enter the actual value for the month. RAG status, variance and trend are computed automatically.",
                  "Switch months from the month-picker on the right to back-fill or review.",
                ],
              },
              {
                icon: MessageSquare,
                title: "Log an MBR or QBR",
                steps: [
                  "Sidebar → Reviews → New review (or open the client and click Record MBR / QBR).",
                  "Fill attendees, action items, NPS (slider 0–10), CSAT (1–5), narrative, next steps.",
                  "Open action items from the last review are shown at the top — update their status before filing the new one.",
                  "Click Save — minutes are emailed to the client's distribution list (currently MOCKED in the email log, sent for real once SMTP is wired).",
                ],
              },
              {
                icon: LayoutDashboard,
                title: "Watch your campaign dashboard",
                steps: [
                  "Open a campaign → the scorecard table shows the current month's RAG at a glance.",
                  "Pick a KPI in the trend chart to see the 12-month line (actual vs benchmark).",
                ],
              },
            ]}
            donts={[
              "Don't skip monthly KPI entry — missing months flip to 'No data' on every dashboard above you.",
              "Don't close an MBR without updating previous action items — that's the main signal for escalation.",
            ]}
          />
        </TabsContent>

        <TabsContent value="branch_manager" className="mt-4 space-y-3">
          <RoleBlock
            role="Branch Manager"
            why="You supervise every campaign in your branch. You can do everything a Process Manager can, across all campaigns you own, and you get branch-level dashboards."
            tasks={[
              {
                icon: LayoutDashboard,
                title: "Your branch dashboard",
                steps: [
                  "Sidebar → Dashboard shows Clients, Campaigns, Revenue YTD, Outstanding, KPI Red count, At-risk clients — filtered to your branch automatically.",
                  "The Branch performance table lists every branch's KPI RAG counts for the current month.",
                ],
              },
              {
                icon: Briefcase,
                title: "Drill into any campaign",
                steps: [
                  "Sidebar → Campaigns (list filtered to your branch) → open any campaign.",
                  "Enter KPI actuals or record a review on behalf of a Process Manager when needed.",
                ],
              },
              {
                icon: AlertTriangle,
                title: "Act on RAG breaches and adverse news",
                steps: [
                  "Any Red KPI for the current month is surfaced in the dashboard 'KPI Red (MTD)' card.",
                  "The 'Adverse intelligence · last 7 days' panel lists market signals on your clients — click through to the client's Intelligence tab.",
                ],
              },
            ]}
            donts={[
              "Don't reassign campaigns without telling the CRM Manager — role scope changes need an Admin update.",
            ]}
          />
        </TabsContent>

        <TabsContent value="regional_manager" className="mt-4 space-y-3">
          <RoleBlock
            role="Regional Manager"
            why="You monitor multiple branches in a region — compare branch performance, spot collections risk, and drive consistency."
            tasks={[
              {
                icon: LayoutDashboard,
                title: "Regional dashboard",
                steps: [
                  "Sidebar → Dashboard — your view is scoped to the branches in your region.",
                  "Grade distribution (pie) shows how A/B/C/D clients are spread across your region.",
                  "Branch performance table highlights RAG counts per branch so you can spot the weakest branch at a glance.",
                ],
              },
              {
                icon: Users,
                title: "Drill down",
                steps: [
                  "Clients → filter by status or grade; open a client for the full 360 view.",
                  "Campaigns → list of all campaigns in your region's branches; open any to review scorecard + trends.",
                ],
              },
            ]}
            donts={[
              "Don't enter monthly KPI data yourself — ask the Process Manager so ownership is clear in the audit log.",
            ]}
          />
        </TabsContent>

        <TabsContent value="coo_ceo" className="mt-4 space-y-3">
          <RoleBlock
            role="COO / CEO"
            why="Read-only portfolio visibility across the entire business. You see everything at a glance and you're the first to know when a client shows adverse signals."
            tasks={[
              {
                icon: LayoutDashboard,
                title: "Portfolio health in 10 seconds",
                steps: [
                  "Sidebar → Dashboard. Top cards: Clients, Campaigns, Revenue YTD, Outstanding, KPI Red (MTD), At-risk clients.",
                  "Grade distribution pie + Branch performance table give a structural view of the book.",
                  "Contracts expiring in 90 days surfaces renewal conversations you need to start.",
                ],
              },
              {
                icon: Brain,
                title: "Adverse intelligence alerts",
                steps: [
                  "The 'Adverse intelligence · last 7 days' panel is your early-warning radar.",
                  "Click a headline to open the client's Intelligence tab and read the full feed.",
                  "You also receive an email alert (currently MOCKED to the email log) every time a new adverse item is classified.",
                ],
              },
              {
                icon: Users,
                title: "Drill when curiosity strikes",
                steps: [
                  "Click any client name → Client 360 shows KPI trends, NPS/CSAT bars, last 6 months billing, latest MBR/QBR.",
                  "You can also trigger an on-demand sweep for any client using 'Run intel sweep' at the top of their profile.",
                ],
              },
            ]}
            donts={[
              "You cannot edit operational data — that's intentional. Request changes via the CRM Manager or Accounts team.",
            ]}
          />
        </TabsContent>
      </Tabs>

      {/* Quick reference */}
      <section className="mptg-card">
        <h2 className="font-heading text-lg font-bold mb-3">Quick reference — every screen in the sidebar</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Row icon={LayoutDashboard} label="Dashboard" desc="Role-scoped KPI cards, grade pie, branch RAG, adverse intel, at-risk, expiring contracts." />
          <Row icon={Users} label="Clients" desc="Full list with filter/search/grade. Click any row for the Client 360 view (tabs incl. Profitability)." />
          <Row icon={Briefcase} label="Campaigns" desc="All campaigns you can see. Multi-branch; click for scorecard + KPI trend chart." />
          <Row icon={Receipt} label="Billing" desc="Invoices, AR summary, aging, bulk upload, CSV template. FY filter." />
          <Row icon={BookOpen} label="Ledger" desc="FY-aware client ledger: opening, debits, credits, closing. Auto from billing + manual entries." />
          <Row icon={MessageSquare} label="Reviews" desc="Every MBR/QBR. Click New review for the structured form. Action-item closure inline." />
          <Row icon={Brain} label="Intelligence" desc="Classified market signals. Admin can 'Sweep all clients' on-demand." />
          <Row icon={FileText} label="Documents" desc="Contracts / SOWs / NDAs per client. Upload, tag, download, (admin) delete." />
          <Row icon={Building2} label="Branches" desc="Admin only — master of delivery branches with manager, campaigns count." />
          <Row icon={Upload} label="Bulk Import" desc="Admin / Accounts — migrate legacy data via CSV templates: branches, users, clients, campaigns, KPIs, opening balances, billing." />
          <Row icon={Shield} label="Admin" desc="Admin only — manage users, roles, branches, reports-to chain." />
          <Row icon={ScrollText} label="Audit Log" desc="Admin only — every change in the system, with user and timestamp." />
          <Row icon={HelpCircle} label="Help" desc="This guide, filtered to your role." />
        </div>
      </section>

      <section className="mptg-card">
        <h2 className="font-heading text-lg font-bold mb-2">Need help?</h2>
        <p className="text-sm text-slate-700">
          Ask your CRM Manager (signed in as: <span className="font-medium">{user?.name}</span> · {ROLE_LABELS[user?.role]}). They have full admin access and can
          adjust your role, scope, or reset passwords. For feature requests, note them in the next MBR and the team will triage.
        </p>
      </section>
    </div>
  );
}

function RoleBlock({ role, why, tasks, donts }) {
  return (
    <div className="space-y-3">
      <div className="mptg-card bg-[#EBF5FB] border-[#2E86C1]/30">
        <div className="mptg-overline">You are the {role}</div>
        <p className="text-sm text-slate-800 mt-1 leading-relaxed">{why}</p>
      </div>
      {tasks.map((t, i) => (
        <div key={i} className="mptg-card">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#1B2A4A] text-white flex items-center justify-center">
              <t.icon className="w-3.5 h-3.5" />
            </div>
            <h3 className="font-heading font-bold text-base">{t.title}</h3>
          </div>
          <ol className="list-decimal pl-10 mt-2 space-y-1.5 text-sm text-slate-700">
            {t.steps.map((s, j) => <li key={j}>{s}</li>)}
          </ol>
        </div>
      ))}
      {donts?.length > 0 && (
        <div className="mptg-card border-[#F39C12]/40 bg-[#FEF5E7]">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#F39C12]" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-[#9C640C]">Don't</h3>
          </div>
          <ul className="list-disc pl-6 space-y-1 text-sm text-slate-800">
            {donts.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, desc }) {
  return (
    <div className="flex items-start gap-3 border border-slate-200 rounded p-3">
      <div className="w-8 h-8 rounded bg-[#EBF5FB] text-[#2E86C1] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
